import React, { useState } from 'react';
import { TargetIcon, FilterIcon, RocketIcon, MessageSquareIcon, ClipboardCheckIcon, HandshakeIcon, BookOpenIcon, UsersIcon, GraduationCapIcon, ShieldCheckIcon, ChevronDownIcon } from 'lucide-react';
export function InteractivePipeline() {
  const [activeStage, setActiveStage] = useState<number | null>(null);
  const stages = [{
    icon: TargetIcon,
    title: 'Attract',
    description: 'Targeted messaging and reach bring the right candidates into the ecosystem.'
  }, {
    icon: MessageSquareIcon,
    title: 'Engage',
    description: 'Initial connection creates interest, alignment, and forward momentum.'
  }, {
    icon: FilterIcon,
    title: 'Screen',
    description: 'Baseline fit, communication ability, and intent are quickly evaluated.'
  }, {
    icon: ClipboardCheckIcon,
    title: 'Qualify',
    description: 'Deeper alignment with the role, expectations, and performance potential is confirmed.'
  }, {
    icon: HandshakeIcon,
    title: 'Commit',
    description: 'High-intent candidates make a clear, personal decision to move forward.'
  }, {
    icon: BookOpenIcon,
    title: 'Onboard',
    description: 'Structure, expectations, and systems are introduced from day one.'
  }, {
    icon: UsersIcon,
    title: 'Integrate',
    description: 'New reps connect to the culture, team, and workflows.'
  }, {
    icon: GraduationCapIcon,
    title: 'Train',
    description: 'Skills and confidence develop through repetition and guided reinforcement.'
  }, {
    icon: RocketIcon,
    title: 'Launch',
    description: 'Reps are prepared to enter active production.'
  }, {
    icon: ShieldCheckIcon,
    title: 'Retain',
    description: 'Consistency, growth, and long-term engagement are reinforced.'
  }];
  const toggleStage = (index: number) => {
    setActiveStage(activeStage === index ? null : index);
  };
  return <section className="section-padding bg-white" data-id="element-648">
      <div className="container-custom" data-id="element-649">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-650">
          <h2 className="mb-6" data-id="element-651">The 10 Essential Recruiting Stages</h2>
          <p className="text-xl text-gray-600 leading-relaxed" data-id="element-652">
            Each rep moves through a structured, intentional process. This isn't
            a "funnel" — it's an operational pipeline designed to improve
            consistency, commitment, and performance.
          </p>
        </div>

        {/* Desktop Horizontal Pipeline */}
        <div className="hidden lg:block max-w-7xl mx-auto" data-id="element-653">
          {/* Stage Buttons */}
          <div className="flex justify-between gap-2 mb-8" data-id="element-654">
            {stages.map((stage, index) => {
            const Icon = stage.icon;
            const isActive = activeStage === index;
            return <button key={index} onClick={() => toggleStage(index)} className={`flex-1 group transition-all duration-300 ${isActive ? 'transform scale-105' : 'hover:transform hover:scale-105'}`} data-id="element-655">
                  <div className={`rounded-xl p-4 border-2 transition-all duration-300 ${isActive ? 'bg-slate border-slate shadow-lg' : 'bg-white border-gray-200 hover:border-slate'}`} data-id="element-656">
                    <div className={`w-12 h-12 mx-auto mb-2 rounded-lg flex items-center justify-center transition-colors ${isActive ? 'bg-white' : 'bg-navy'}`} data-id="element-657">
                      <Icon className={`h-6 w-6 transition-colors ${isActive ? 'text-slate' : 'text-slate'}`} data-id="element-658" />
                    </div>
                    <div className={`text-xs font-bold mb-1 ${isActive ? 'text-white' : 'text-gray-600'}`} data-id="element-659">
                      {index + 1}
                    </div>
                    <div className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-navy'}`} data-id="element-660">
                      {stage.title}
                    </div>
                    <ChevronDownIcon className={`h-4 w-4 mx-auto mt-2 transition-transform duration-300 ${isActive ? 'text-white rotate-180' : 'text-gray-400 group-hover:text-slate'}`} data-id="element-661" />
                  </div>
                </button>;
          })}
          </div>

          {/* Expanded Description */}
          <div className="min-h-[120px]" data-id="element-662">
            {activeStage !== null && <div className="bg-gray-50 rounded-xl p-8 border-2 border-slate animate-fadeIn" key={activeStage} data-id="element-663">
                <div className="max-w-3xl mx-auto text-center" data-id="element-664">
                  <h3 className="text-2xl mb-4 text-navy" data-id="element-665">
                    Stage {activeStage + 1}: {stages[activeStage].title}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-666">
                    {stages[activeStage].description}
                  </p>
                </div>
              </div>}
          </div>
        </div>

        {/* Mobile Accordion */}
        <div className="lg:hidden max-w-2xl mx-auto space-y-3" data-id="element-667">
          {stages.map((stage, index) => {
          const Icon = stage.icon;
          const isActive = activeStage === index;
          return <button key={index} onClick={() => toggleStage(index)} className="w-full text-left" data-id="element-668">
                <div className={`rounded-xl p-4 border-2 transition-all ${isActive ? 'bg-slate border-slate' : 'bg-white border-gray-200'}`} data-id="element-669">
                  <div className="flex items-center justify-between" data-id="element-670">
                    <div className="flex items-center gap-4" data-id="element-671">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-white' : 'bg-navy'}`} data-id="element-672">
                        <Icon className={`h-6 w-6 ${isActive ? 'text-slate' : 'text-slate'}`} data-id="element-673" />
                      </div>
                      <div data-id="element-674">
                        <div className={`text-xs font-bold mb-1 ${isActive ? 'text-white' : 'text-gray-600'}`} data-id="element-675">
                          Stage {index + 1}
                        </div>
                        <div className={`text-lg font-semibold ${isActive ? 'text-white' : 'text-navy'}`} data-id="element-676">
                          {stage.title}
                        </div>
                      </div>
                    </div>
                    <ChevronDownIcon className={`h-5 w-5 flex-shrink-0 transition-transform ${isActive ? 'text-white rotate-180' : 'text-gray-400'}`} data-id="element-677" />
                  </div>
                  {isActive && <div className="mt-4 pt-4 border-t border-white/20" data-id="element-678">
                      <p className="text-white leading-relaxed" data-id="element-679">
                        {stage.description}
                      </p>
                    </div>}
                </div>
              </button>;
        })}
        </div>

        <div className="mt-12 text-center" data-id="element-680">
          <div className="inline-block bg-navy/5 rounded-xl p-6 max-w-2xl border border-navy/10" data-id="element-681">
            <p className="text-gray-700 leading-relaxed" data-id="element-682">
              <span className="font-bold text-navy" data-id="element-683">
                This is not a one-time journey.
              </span>{' '}
              It's a continuous loop that keeps delivering qualified candidates
              while supporting the development of your active team.
            </p>
          </div>
        </div>
      </div>

      <style data-id="element-684">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </section>;
}